import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Users, GraduationCap, BookOpen, AlertCircle, 
  Wallet, IndianRupee, CalendarCheck, BellRing,
  Award, TrendingUp
} from 'lucide-react';
import APIRequest from '../api/APIRequest';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [statsData, setStatsData] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const apiRequest = new APIRequest();
      try {
        const stats = await apiRequest.request("/admin/getStats", "GET", null, user?.token)
        if (stats.success) {
          setStatsData(stats.statsData);
        } else {
        }
      } catch (error) {
      }
    };
    fetchStats();
  }, [user?.token]);

  const stats = [
    {
      title: 'Total Students',
      value: statsData?.totalStudents,
      icon: Users,
      color: 'bg-blue-500',
      changeType: 'increase',
      path: '/students'
    },
    {
      title: 'Total Staff',
      value: statsData?.totalTeachers,
      icon: GraduationCap,
      color: 'bg-emerald-500',
      changeType: 'increase',
      path: '/staff'
    },
    {
      title: 'Total Classes',
      value: statsData?.totalClasses,
      icon: BookOpen,
      color: 'bg-orange-500',
      changeType: 'increase',
      path: '/classes'
    },
  ];

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="animate-slideInDown">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back! Here's what's happening at your school.</p>
      </div>

      {/* Premium Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 animate-slideInUp">
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <div 
              key={stat.title} 
              onClick={() => navigate(stat.path)}
              className="glass-card rounded-[2rem] p-8 hover:shadow-2xl hover:shadow-blue-200/40 transition-all duration-500 transform hover:-translate-y-2 cursor-pointer group premium-shadow"
            >
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <div className={`p-4 rounded-2xl ${stat.color} shadow-lg shadow-blue-100 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{stat.title.split(' ')[0]}</p>
                    <p className="text-sm font-bold text-[#1B6CA8] mt-1">View All</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-4xl font-black text-slate-800 tracking-tight group-hover:text-[#1B6CA8] transition-colors">{stat.value}</h3>
                  <p className="text-sm font-bold text-slate-500 mt-1">{stat.title}</p>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full primary-gradient-bg w-2/3 group-hover:w-full transition-all duration-1000"></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Primary Analytics Row: Attendance & Fees */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-slideInUp" style={{animationDelay: '0.1s'}}>
        {/* Attendance Widget */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 transform transition-all duration-300 hover:shadow-xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Today's Attendance</h3>
              <p className="text-sm text-gray-500 mt-1">Real-time presence across all classes</p>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <CalendarCheck size={24} />
            </div>
          </div>
          
          <div className="flex items-center gap-8">
            <div className="relative w-32 h-32 flex-shrink-0 flex justify-center items-center">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-gray-100"
                  strokeWidth="3"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-emerald-500 stroke-current drop-shadow-md"
                  strokeWidth="3"
                  strokeDasharray={`${statsData?.todayAttendanceSnapshot?.percentage || 0}, 100`}
                  strokeLinecap="round"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="absolute flex flex-col items-center justify-center">
                <span className="text-3xl font-black text-gray-800">{statsData?.todayAttendanceSnapshot?.percentage || 0}%</span>
              </div>
            </div>
            
            <div className="flex-1 space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm font-semibold text-gray-700">Present</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{statsData?.todayAttendanceSnapshot?.present || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-rose-50 border border-rose-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-rose-500"></div>
                  <span className="text-sm font-semibold text-gray-700">Absent/Unmarked</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {Math.max(0, (statsData?.todayAttendanceSnapshot?.total || 0) - (statsData?.todayAttendanceSnapshot?.present || 0))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Fees Overview Widget */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 transform transition-all duration-300 hover:shadow-xl flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Financial Overview</h3>
              <p className="text-sm text-gray-500 mt-1">Pending vs Collected Fees</p>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Wallet size={24} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase">Total Collected</p>
              <p className="text-2xl font-black text-emerald-600 mt-1">
                ₹{statsData?.feesStatistics?.statusBreakdown?.find(s => s.status === 'paid')?.totalAmount?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-100">
              <p className="text-xs font-semibold text-gray-500 uppercase">Overdue Balance</p>
              <p className="text-2xl font-black text-rose-600 mt-1">
                ₹{statsData?.feesStatistics?.statusBreakdown?.find(s => s.status === 'overdue')?.totalAmount?.toLocaleString() || 0}
              </p>
            </div>
          </div>

          <div className="flex-1 mt-auto">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
              <AlertCircle size={14} className="text-rose-500" /> Action Needed: Top Defaults
            </h4>
            <div className="space-y-3">
              {statsData?.topOverdueStudents && statsData.topOverdueStudents.length > 0 ? (
                statsData.topOverdueStudents.map((fee, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => navigate('/fees')}>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900">{fee.studentName}</span>
                      <span className="text-xs text-gray-500">{fee.className} - {fee.classSection}</span>
                    </div>
                    <div className="text-right flex flex-col">
                      <span className="text-sm font-bold text-rose-600">₹{fee.amount.toLocaleString()}</span>
                      <span className="text-[10px] text-gray-400 bg-white px-2 py-0.5 rounded-full border">{Math.floor(fee.daysOverdue)} days late</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 bg-gray-50 rounded-lg">
                  <Award size={24} className="mx-auto text-emerald-400 mb-2" />
                  <p className="text-sm text-gray-500">All students are paid up!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-slideInUp" style={{animationDelay: '0.2s'}}>
        
        {/* Notice Board Feed Widget */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 transform transition-all duration-300 hover:shadow-xl flex flex-col">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 text-amber-500 rounded-lg">
                <BellRing size={20} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Recent Notices</h3>
            </div>
            <button onClick={() => navigate('/notice-board')} className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors">
              View Board
            </button>
          </div>
          <div className="p-0 overflow-hidden flex-1">
            {statsData?.recentNotices && statsData.recentNotices.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {statsData.recentNotices.map((notice, idx) => (
                  <div key={idx} className="p-5 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate('/notice-board')}>
                    <div className="flex justify-between items-start mb-2">
                      <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${
                        notice.priority === 'Urgent' ? 'bg-rose-100 text-rose-700' :
                        notice.priority === 'Important' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {notice.priority}
                      </span>
                      <span className="text-xs font-semibold text-gray-400">{new Date(notice.createdAt).toLocaleDateString()}</span>
                    </div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1">{notice.title}</h4>
                    <p className="text-xs text-gray-500 line-clamp-2">{notice.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center text-gray-400">
                <BellRing size={32} className="mb-3 opacity-20" />
                <p className="text-sm text-gray-500">No active notices.</p>
              </div>
            )}
          </div>
        </div>

        {/* Active Classes */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 transform transition-all duration-300 hover:shadow-xl flex flex-col">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 text-indigo-500 rounded-lg">
                <BookOpen size={20} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Class Enrollment</h3>
            </div>
            <button onClick={() => navigate('/classes')} className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors">
              Manage
            </button>
          </div>
          <div className="p-4 space-y-3 flex-1 overflow-y-auto max-h-[400px]">
              {statsData?.activeClasses && statsData.activeClasses.length > 0 ? (
                statsData.activeClasses.map((classItem) => (
                  <div key={classItem._id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 transition-all duration-200 hover:bg-indigo-50/30 hover:shadow-sm">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold border border-indigo-100">
                        {classItem.name.substring(0,2)}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{classItem.name} {classItem.section && `- ${classItem.section}`}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Users size={12} /> {classItem.teacherName || 'No Teacher Assigned'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-end gap-1 justify-end">
                        <span className="text-lg font-black text-gray-900">{classItem.enrolledCount}</span>
                        <span className="text-xs font-semibold text-gray-400 mb-1">/ {classItem.class_capacity}</span>
                      </div>
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${classItem.enrolledCount >= classItem.class_capacity ? 'bg-rose-500' : 'bg-emerald-500'}`} 
                          style={{width: `${Math.min(100, Math.round((classItem.enrolledCount / (classItem.class_capacity || 1)) * 100))}%`}}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center py-12 text-center text-gray-400">
                  <TrendingUp size={32} className="mb-3 opacity-20" />
                  <p className="text-sm text-gray-500">No active classes found.</p>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;